import { setStatus } from '../render/plan_renderer.js?v=20260904f';
import { t } from '../i18n/i18n_service.js?v=20260722a';
import { openConfirmDialog, openTextPromptDialog } from '../ui/dialog_service.js?v=20260817b';
import { escapeHtml } from '../utils/html_utils.js?v=20260706d';
import {
  executeCoreGroupSidebarAction,
  getCoreGroupSidebarActions,
  getSingleActiveCoreGroup
} from '../objects/group_service.js?v=20260904c';
import {
  activeDrawingSheet,
  activeDrawingSheetCoreGroup,
  renameActiveCoreGroupTitleSuffix,
  renameDrawingSheet
} from './drawing_sheet_service.js?v=20260904c';

const CORE_GROUP_ACTION_SURFACE = 'drawing-sheet-title-card';

function coreGroupActionIdentity(group = null) {
  if (!group) return '';
  const systemId = String(group.systemId || group.systemKey || '').trim();
  const groupId = String(group.groupTitleEntryId || group.key || group.groupId || group.id || '').trim();
  return systemId && groupId ? `${systemId}:${groupId}` : '';
}

function activeCoreGroupForAction(expectedIdentity) {
  const group = activeDrawingSheetCoreGroup(getSingleActiveCoreGroup());
  return coreGroupActionIdentity(group) === expectedIdentity ? group : null;
}

function coreGroupActionContext() {
  return {
    surface: CORE_GROUP_ACTION_SURFACE,
    drawingSheet: activeDrawingSheet()
  };
}

function availableCoreGroupAction(group, actionId) {
  return getCoreGroupSidebarActions(group, coreGroupActionContext())
    .find(action => action.id === actionId && action.disabled !== true) || null;
}

function renderCoreGroupSidebarActions(actions = []) {
  if (!actions.length) return '';
  return `
    <div class="core-group-sidebar-actions" data-core-group-sidebar-actions>
      ${actions.map(action => `
        <button
          class="btn core-group-sidebar-action is-${escapeHtml(action.variant)}"
          type="button"
          data-core-group-sidebar-action="${escapeHtml(action.id)}"
          data-core-group-action-disabled="${action.disabled ? 'true' : 'false'}"
          title="${escapeHtml(action.title)}"
          ${action.disabled ? 'disabled' : ''}
        >${escapeHtml(action.label)}</button>
      `).join('')}
    </div>
  `;
}

function setCoreGroupActionsBusy(host, busy, activeAction = null) {
  host.querySelectorAll('[data-core-group-sidebar-action]').forEach(button => {
    button.disabled = busy || button.dataset.coreGroupActionDisabled === 'true';
    button.setAttribute('aria-busy', busy ? 'true' : 'false');
  });
  if (!busy || !activeAction?.pendingLabel) return;
  const button = [...host.querySelectorAll('[data-core-group-sidebar-action]')]
    .find(candidate => candidate.dataset.coreGroupSidebarAction === activeAction.id);
  if (button) button.textContent = activeAction.pendingLabel;
}

function renderDrawingSheetTitlePanel(host) {
  if (!host) return false;
  const sheet = activeDrawingSheet();
  if (!sheet) {
    host.innerHTML = '';
    host.hidden = true;
    return false;
  }
  const selectedGroup = activeDrawingSheetCoreGroup(getSingleActiveCoreGroup());
  const selectedGroupActions = selectedGroup
    ? getCoreGroupSidebarActions(selectedGroup, {
        surface: CORE_GROUP_ACTION_SURFACE,
        drawingSheet: sheet
      })
    : [];
  host.hidden = false;
  host.innerHTML = `
    <div class="drawing-sheet-title-panel-stack">
      <div class="card drawing-sheet-title-card">
        <button class="drawing-sheet-title-edit" type="button" data-edit-drawing-sheet-title title="${escapeHtml(t('sidebar_links.zeichnungsebenen.button.umbenennen', 'Zeichnungsebene umbenennen'))}">
          <span>${escapeHtml(sheet.title)}</span>
          <span class="drawing-sheet-title-edit-icon" aria-hidden="true">✎</span>
        </button>
      </div>
      ${selectedGroup ? `
        <div class="card drawing-sheet-title-card core-group-title-card">
          <button class="drawing-sheet-title-edit core-group-title-edit" type="button" data-edit-core-group-title title="Gruppe umbenennen">
            <span class="core-group-title-text"><span class="core-group-title-base">${escapeHtml(selectedGroup.groupTitleBase)}</span> <span class="core-group-title-suffix">${escapeHtml(selectedGroup.groupTitleSuffix)}</span></span>
            <span class="drawing-sheet-title-edit-icon" aria-hidden="true">✎</span>
          </button>
          ${renderCoreGroupSidebarActions(selectedGroupActions)}
        </div>
      ` : ''}
    </div>
  `;
  host.querySelector('[data-edit-drawing-sheet-title]')?.addEventListener('click', async () => {
    const title = await openTextPromptDialog({
      title: t('modal.zeichnungsebene.titel.umbenennen', 'Zeichnungsebene umbenennen'),
      label: t('modal.zeichnungsebene.label.name', 'Name der Zeichnungsebene'),
      initialValue: sheet.title,
      confirmLabel: t('modal.zeichnungsebene.button.uebernehmen', 'Übernehmen')
    });
    if (title === null) return;
    try {
      renameDrawingSheet(sheet.id, title);
      setStatus(`Zeichnungsebene umbenannt: ${String(title).trim()}`);
    } catch (error) {
      setStatus(error.message || 'Zeichnungsebene konnte nicht umbenannt werden.');
    }
  });
  host.querySelector('[data-edit-core-group-title]')?.addEventListener('click', async () => {
    const suffix = await openTextPromptDialog({
      title: 'Gruppenname anpassen',
      label: `Namenszusatz nach „${selectedGroup.groupTitleBase}“`,
      initialValue: selectedGroup.groupTitleSuffix,
      confirmLabel: t('modal.zeichnungsebene.button.uebernehmen', 'Übernehmen')
    });
    if (suffix === null) return;
    try {
      const displayName = renameActiveCoreGroupTitleSuffix(selectedGroup, suffix);
      setStatus(`Gruppe umbenannt: ${displayName}`);
    } catch (error) {
      setStatus(error.message || 'Gruppe konnte nicht umbenannt werden.');
    }
  });
  let coreGroupActionPending = false;
  const selectedGroupIdentity = coreGroupActionIdentity(selectedGroup);
  host.querySelectorAll('[data-core-group-sidebar-action]').forEach(button => {
    button.addEventListener('click', async event => {
      event.preventDefault();
      if (coreGroupActionPending) return;
      const actionId = String(event.currentTarget?.dataset?.coreGroupSidebarAction || '').trim();
      let liveGroup = activeCoreGroupForAction(selectedGroupIdentity);
      let action = liveGroup ? availableCoreGroupAction(liveGroup, actionId) : null;
      if (!liveGroup || !action) {
        setStatus(action?.unavailableMessage || 'Die Gruppenaktion ist nicht mehr verfügbar.');
        renderDrawingSheetTitlePanel(host);
        return;
      }

      coreGroupActionPending = true;
      setCoreGroupActionsBusy(host, true, action);
      try {
        if (action.confirm) {
          const confirmed = await openConfirmDialog(action.confirm);
          if (!confirmed) return;
          liveGroup = activeCoreGroupForAction(selectedGroupIdentity);
          action = liveGroup ? availableCoreGroupAction(liveGroup, actionId) : null;
          if (!liveGroup || !action) {
            setStatus(action?.unavailableMessage || 'Die Gruppenaktion ist nicht mehr verfügbar.');
            return;
          }
        }

        const result = await executeCoreGroupSidebarAction(
          liveGroup,
          action.id,
          coreGroupActionContext()
        );
        if (result?.handled === true) {
          setStatus(result.message || action.successMessage || `${action.label} ausgeführt.`);
        } else {
          setStatus(action.unavailableMessage || 'Die Gruppenaktion konnte nicht ausgeführt werden.');
        }
      } catch (error) {
        setStatus(error?.message || 'Die Gruppenaktion konnte nicht ausgeführt werden.');
      } finally {
        coreGroupActionPending = false;
        if (host.isConnected !== false) renderDrawingSheetTitlePanel(host);
      }
    });
  });
  return true;
}

export { renderDrawingSheetTitlePanel };
